import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { resolveLandingRoute } from "../lib/experienceResolver";
import { REGISTER } from "../constants/testIds";
import { toast } from "sonner";
import BtcLogo from "../components/BtcLogo";

const ROLES = [
  { v: "patient", l: "Patient" },
  { v: "survivor", l: "Survivor" },
  { v: "caregiver", l: "Carer" },
  { v: "volunteer", l: "Volunteer" },
  { v: "hospital", l: "Hospital Trust" },
  { v: "organization", l: "Charity / Support Service" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [role, setRole] = useState("patient");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resolverFailed, setResolverFailed] = useState(false);

  const attemptResolve = async () => {
    try {
      const dest = await resolveLandingRoute();
      setResolverFailed(false);
      navigate(dest, { replace: true });
    } catch (_e) {
      // Phase 2 verification §4 — no silent workspace fallback.
      setResolverFailed(true);
      setErr("We could not determine where to send you. Please retry.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setResolverFailed(false);
    if (password !== confirm) return setErr("Passwords do not match.");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await register({ name: name.trim(), email: email.trim(), password, role });
      toast.success("Welcome to BTC. Your portal is ready.");
      await attemptResolve();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Registration failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <main id="main-content" className="flex-1 bg-white px-6 py-12 lg:px-16 flex flex-col">
        <Link to="/" className="inline-flex items-center gap-2">
          <BtcLogo className="h-12 w-auto" />
        </Link>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            <p className="btc-eyebrow mb-3">JOIN BTC FREE</p>
            <h1 className="text-4xl font-extrabold tracking-tight text-[#0B132B] mb-3">
              Create your account.
            </h1>
            <p className="text-[#4B5563] mb-8">
              Already a member?{" "}
              <Link to="/login" data-testid={REGISTER.loginLink} className="text-[#1A5FAB] font-semibold">
                Sign in
              </Link>
              .
            </p>
            <form onSubmit={onSubmit} className="space-y-4" aria-label="Create a BTC account">
              <div>
                <label htmlFor="register-name" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 block">Full name</label>
                <input
                  id="register-name"
                  data-testid={REGISTER.nameInput}
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="register-role" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 block">I am a</label>
                <select
                  id="register-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                >
                  {ROLES.map((r) => (
                    <option key={r.v} value={r.v}>{r.l}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="register-email" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 block">Email</label>
                <input
                  id="register-email"
                  data-testid={REGISTER.emailInput}
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="register-password" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 block">Password</label>
                  <input
                    id="register-password"
                    data-testid={REGISTER.passwordInput}
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                    placeholder="8+ characters"
                  />
                </div>
                <div>
                  <label htmlFor="register-confirm" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 block">Confirm</label>
                  <input
                    id="register-confirm"
                    data-testid={REGISTER.passwordConfirmInput}
                    type="password"
                    required
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                    placeholder="Repeat"
                  />
                </div>
              </div>
              {err && <div data-testid="register-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{err}</div>}
              {resolverFailed ? (
                <button
                  type="button"
                  data-testid="register-resolver-retry"
                  onClick={attemptResolve}
                  className="w-full btc-btn-primary justify-center"
                >
                  Retry
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  data-testid={REGISTER.submitButton}
                  disabled={busy}
                  className="w-full btc-btn-primary justify-center disabled:opacity-60"
                >
                  {busy ? "Creating account..." : "Create my account"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              <p className="text-xs text-[#4B5563] text-center">
                By signing up, you agree to our Terms and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </main>
      <div
        className="hidden lg:flex flex-1 relative items-end p-12 bg-cover bg-center"
        style={{
          backgroundImage: "url('/img/hero.jpg')",
        }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/90 via-[#0B132B]/40 to-transparent" />
        <div className="relative text-white max-w-md">
          <Heart className="w-10 h-10 text-[#23803F] mb-4" fill="#2D9B4E" />
          <h2 className="text-3xl font-extrabold tracking-tight mb-3 leading-tight">
            Built for everyone touched by cancer.
          </h2>
          <p className="text-white/80 text-lg">
            Patients. Caregivers. Survivors. Volunteers. Together in one ecosystem.
          </p>
        </div>
      </div>
    </div>
  );
}
